import React, { useEffect, useState } from "react"
import { isMobile } from "react-device-detect"
import _get from "lodash/get"

import { getConfig } from "@/constants/config"
import { getPDPUrlWithConfig, sanitizeTextForAnalytics } from "@/utils/helper"

import { getProductsBySku } from "@/components/sharedCart/shareCartHelper"
import MissingImage from "@/public/images/image-missing.png"

import styles from "@/components/ProductDetail/v3/MoreFromCollection/index.module.scss"
import { analyticsMoreFromCollectionParts } from "@/components/ProductDetail/v3/MoreFromCollection/analytics"

const MoreFromCollection = props => {
  const {
    sku = "",
    pdpJson = {},
    presetConfigObject = {},
    productType = "",
    partReplacementSuggestions = "",
    i18n: { partNumber = "", partName = "" } = {},
    productInfo = {},
  } = props

  const [data, setData] = useState([])
  const [recommendations, setRecommendations] = useState([])
  const [title, setTitle] = useState("")
  const [config, setConfig] = useState({})
  const [general, setGeneral] = useState(null)
  const [plpServletPath, setPlpServletPath] = useState(null)
  if (!pdpJson.ctRecommendations) {
    pdpJson.ctRecommendations = []
  }
  const { title: replacementTitle, producttype } =
    (0 in pdpJson.ctRecommendations && pdpJson.ctRecommendations[0]) ?? {}

  useEffect(() => {
    getConfig().then(config => {
      setConfig(config)
      setGeneral(config.general)
      setPlpServletPath(config.plpServletPath)
    })
  }, [])

  useEffect(() => {
    if (productType === producttype) {
      if (partReplacementSuggestions) {
        setTitle(replacementTitle)
        setRecommendations(
          partReplacementSuggestions
            .split(",")
            .map(product => ({ sku: product }))
        )
      } else {
        setTitle("")
        setRecommendations([])
      }
    }
  }, [sku, partReplacementSuggestions])

  /**
   * Selects Appropriate preset for more collections and appends it to the image url based on screen size
   * as container size cannot be considered in this scenario
   *
   * @param {string} uUrl     the formatted url which contains swatch url and id
   * @return {string} final url with correct preset appended
   */
  const appendPreset = uUrl => {
    let sPreset = ""
    if (window.innerWidth < 660) {
      sPreset = presetConfigObject.MoreCollectionMobilePotrait ?? ""
    } else if (isMobile) {
      sPreset = presetConfigObject.MoreCollectionMobileLandscape ?? ""
    } else {
      sPreset = presetConfigObject.MoreCollectionDesktop ?? ""
    }

    return `${uUrl}?${sPreset}`
  }

  /**
   * Formats the base url using the logic of swatch url + id + preset, that way it can be
   * used to render the final image to the DOM
   *
   * @param {string} uUrl   the base url which will come from the api response
   * @return {string}
   */
  const formatUrl = uUrl => {
    const sUrlPath = uUrl.split("?")[0]?.split("/")
    const sUrlId = sUrlPath[sUrlPath.length - 1]
    const swatchUrl = pdpJson.swatchUrl
    const uFinalUrl = appendPreset(`${swatchUrl}${sUrlId}`)

    return uFinalUrl
  }

  useEffect(() => {
    const getProducts = async () => {
      const skus =
        recommendations.map(product => {
          if (product.sku) {
            return product.sku.replace("K-", "")
          }
        }) ?? []
      if (skus.length) {
        try {
          const d = await getProductsBySku(skus)
          const productData = skus.map(sku => {
            const item = d.find(
              p =>
                p.masterVariant.sku === sku ||
                p.variants.find(v => v.sku === sku)
            )
            const variant =
              item.masterVariant.sku === sku
                ? item.masterVariant
                : item.variants.find(v => v.sku === sku)
            const channels = variant?.availability?.channels ?? {}
            const inStock = channels[0]?.availableQuantity ?? 0
            const image =
              variant?.images?.length > 0
                ? formatUrl(variant.images[0].url)
                : MissingImage
            const category = variant.attributes.filter(
              attr => attr.name === "ProductLocalCategory"
            )[0]
            const skuID = _get(
              variant.attributes.find(
                attr => attr.name === "CustomerFacingSKU"
              ),
              "value",
              ""
            )
            return {
              name: item.name[config.internationalization?.locale],
              slug: item.slug[config.internationalization?.locale],
              inStock,
              image,
              category: category?.value,
              sku: variant?.sku,
              skuID: skuID,
            }
          })
          setData(productData)
        } catch (e) {
          setData([])
        }
      } else {
        setData([])
      }
    }
    getProducts()
  }, [recommendations, config])

  const handlePdpMoreFromCollectionParts = (
    urlWithSku = "",
    title = "",
    sku = ""
  ) => {
    analyticsMoreFromCollectionParts(urlWithSku, title, sku, productInfo)
  }

  return (
    data &&
    data.length > 0 && (
      <div className={styles.moreFromCollectionWrapper}>
        <div
          className={`pdp-more-from-collection ${
            productType === producttype ? "parts" : ""
          }`}
        >
          <p className="pdp-more-from-collection__title">{title}</p>
          {productType === producttype ? (
            <ul className="pdp-more-from-collection__parts">
              <li>
                <div>{partNumber}</div>
                <div>{partName}</div>
              </li>
              {data.map((item, i) => {
                const url = getPDPUrlWithConfig(
                  item.category,
                  item.slug,
                  plpServletPath,
                  general
                )
                const urlWithSku = `${url}${
                  url?.includes("?type=part") ? "&" : "?"
                }skuId=${item.sku}`

                return (
                  <li key={i}>
                    <div>
                      <a
                        onClick={() =>
                          handlePdpMoreFromCollectionParts(
                            urlWithSku,
                            sanitizeTextForAnalytics(item?.name?.toLowerCase()),
                            item?.sku
                          )
                        }
                        href={urlWithSku}
                        alt={item.name}
                      >
                        {item.skuID}
                      </a>
                    </div>
                    <div className="pdp-more-from-collection__part-name">
                      {item.name}
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <div className="pdp-more-from-collection__track">
              {data.map((item, i) => (
                <div className="pdp-more-from-collection__product" key={i}>
                  <a
                    className="gbh-data-layer"
                    data-gbh-data-layer={`{"eventAction":"pdp:more from collection:carousel image","eventName":"pdp:more from collection:carousel image","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"${item.name}","internalLinkPosition":"pdp","internalLinkType":"pdp:navigation","internalLinkZoneName":"pdp:cross-sell:more from collection","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
                    href={getPDPUrlWithConfig(
                      item.category,
                      item.slug,
                      plpServletPath,
                      general
                    )}
                    alt={item.name}
                  >
                    <img
                      src={item.image}
                      onError={e => (e.target.src = MissingImage)}
                      className="pdp-more-from-collection__image"
                      alt={item.name}
                    />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  )
}

export default MoreFromCollection
